import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ELEMENT_CATALOG } from './icons';
import { LINE_COLORS, type Line, type PlacedItem } from './types';

interface LinesPanelProps {
  lines: Line[];
  activeLineId: string;
  items: PlacedItem[];
  onSelectLine: (lineId: string) => void;
  onAddLine: () => void;
  onRenameLine: (lineId: string, name: string) => void;
  onDeleteLine: (lineId: string) => void;
}

function isLineControlledItemType(type: string): boolean {
  const group = ELEMENT_CATALOG[type]?.group;
  return group === 'light' || type === 'switch';
}

export function LinesPanel({
  lines,
  activeLineId,
  items,
  onSelectLine,
  onAddLine,
  onRenameLine,
  onDeleteLine,
}: LinesPanelProps) {
  const { t } = useTranslation();
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const canAddLine = lines.length < LINE_COLORS.length;
  const canDeleteLine = lines.length > 1;

  const itemCounts = new Map<string, number>();
  for (const item of items) {
    if (!isLineControlledItemType(item.type) || !item.lineId) continue;
    const lineId = item.lineId;
    itemCounts.set(lineId, (itemCounts.get(lineId) ?? 0) + 1);
  }

  const startEditing = (line: Line) => {
    setEditingLineId(line.id);
    setDraftName(line.name);
  };

  const cancelEditing = () => {
    setEditingLineId(null);
    setDraftName('');
  };

  const saveEditing = () => {
    if (!editingLineId) return;
    const nextName = draftName.trim();
    if (nextName) onRenameLine(editingLineId, nextName);
    cancelEditing();
  };

  const handleEditorKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') saveEditing();
    if (event.key === 'Escape') cancelEditing();
  };

  return (
    <div className="border-b border-border bg-surface-2">
      <div className="flex items-center justify-between gap-2 px-3 py-3">
        <div className="font-sans text-sm font-semibold text-ink">{t('floor_plan.lines')}</div>
        <button
          type="button"
          onClick={onAddLine}
          disabled={!canAddLine}
          className="flex h-7 w-7 items-center justify-center rounded border border-border-2 text-sm font-medium text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
          title={t('floor_plan.add_line')}
          aria-label={t('floor_plan.add_line')}
        >
          +
        </button>
      </div>

      <div className="flex flex-col gap-1 px-2 pb-3">
        {lines.map(line => {
          const isActive = line.id === activeLineId;
          const isEditing = line.id === editingLineId;
          const count = itemCounts.get(line.id) ?? 0;

          return (
            <div
              key={line.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectLine(line.id)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectLine(line.id);
                }
              }}
              onDoubleClick={() => startEditing(line)}
              className={[
                'group flex items-center gap-2 rounded border px-2 py-1.5 text-left transition-colors',
                isActive
                  ? 'border-accent/40 bg-accent/10 text-accent'
                  : 'border-transparent text-ink-3 hover:border-border hover:bg-surface-3 hover:text-ink',
              ].join(' ')}
            >
              <span
                className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: line.color }}
              />

              <span className="min-w-0 flex-1">
                {isEditing ? (
                  <input
                    value={draftName}
                    onChange={event => setDraftName(event.target.value)}
                    onBlur={saveEditing}
                    onKeyDown={handleEditorKeyDown}
                    onClick={event => event.stopPropagation()}
                    className="w-full rounded border border-border-2 bg-surface px-1.5 py-1 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    autoFocus
                  />
                ) : (
                  <span className="block truncate text-xs font-medium">{line.name}</span>
                )}
              </span>

              <span className="font-mono text-[11px] text-ink-4">{count}</span>

              {!isEditing && (
                <span className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      startEditing(line);
                    }}
                    className="rounded px-1 text-[11px] text-ink-4 hover:bg-surface hover:text-ink"
                    title={t('floor_plan.rename')}
                    aria-label={t('floor_plan.rename')}
                  >
                    ✎
                  </button>
                  {canDeleteLine && (
                    <button
                      type="button"
                      onClick={event => {
                        event.stopPropagation();
                        onDeleteLine(line.id);
                      }}
                      className="rounded px-1 text-[11px] text-ink-4 hover:bg-danger/10 hover:text-danger"
                      title={t('floor_plan.delete')}
                      aria-label={t('floor_plan.delete')}
                    >
                      ×
                    </button>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
